import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  IconButton,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import EmailListService from '../../../services/EmailListService';

const EditListModal = ({ open, onClose, onSave, list }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  
  // Estados para el paso 1 (clientes)
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientsFullData, setSelectedClientsFullData] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  const [clientPaginationModel, setClientPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [clientLoading, setClientLoading] = useState(false);
  const [clientTotalRows, setClientTotalRows] = useState(0);
  const [loadingListContacts, setLoadingListContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isUpdatingSelectionRef = useRef(false);

  const steps = [
    t('list_details') || 'Detalles de la Lista',
    t('add_clients') || 'Agregar Clientes'
  ];

  // Función para mapear contacts a formato de clientes
  const mapContactsToClients = (contacts) => {
    return contacts
      .filter(contact => contact.id && contact.id !== '-' && contact.id !== 'null' && String(contact.id).toLowerCase() !== 'null')
      .map((contact) => {
        const clientType = contact.type || 'B2C';
        const clientId = String(contact.id).trim();
        
        return {
          id: clientType === 'B2B' ? `b2b_${clientId}` : `b2c_${clientId}`,
          clientId: clientId,
          clientType: clientType,
          displayName: contact.name || `Cliente ${clientId}`,
          identifier: clientId,
          email: contact.email,
          ...contact
        };
      });
  };

  // Cargar datos de la lista cuando se abre el modal
  useEffect(() => {
    if (open && list) {
      console.log('[EditListModal] Modal opened with list:', list);
      setActiveStep(0);
      setFormData({
        name: list.name || '',
        description: list.description || ''
      });
      setSelectedClients([]);
      setSelectedClientsFullData([]);
      setClientSearchTerm('');
      setClientTypeFilter('all');
      setClientPaginationModel({ page: 0, pageSize: 10 });
    }
  }, [open, list]);

  // Cargar contacts de la lista cuando se entra al paso 1
  useEffect(() => {
    const loadListContacts = async () => {
      if (activeStep === 1 && list && list.id) {
        try {
          setLoadingListContacts(true);
          const listId = isNaN(list.id) ? list.id : parseInt(list.id);
          console.log('[EditListModal] Loading list contacts from backend (step 1):', listId);
          
          // Hacer GET de la lista completa del backend para obtener los contacts
          const listData = await EmailListService.getListById(listId);
          
          console.log('[EditListModal] List data loaded:', {
            id: listData?.id,
            name: listData?.name,
            contactsCount: listData?.contacts?.length || 0
          });
          
          // Precargar TODOS los clientes si vienen en la respuesta
          const listContacts = listData?.contacts || [];
          if (listContacts.length > 0) {
            console.log('[EditListModal] Precargando TODOS los clientes de la lista:', listContacts.length);
            const preloadedClients = mapContactsToClients(listContacts);
            const selectedIds = preloadedClients.map(c => c.id);
            console.log('[EditListModal] IDs de clientes precargados:', selectedIds.slice(0, 5), '... (total:', selectedIds.length, ')');
            
            // Usar el flag para evitar que el DataGrid interfiera
            isUpdatingSelectionRef.current = true;
            setSelectedClients(selectedIds);
            setSelectedClientsFullData(preloadedClients);
            console.log('[EditListModal] Clientes precargados - selectedClients:', selectedIds.length, 'selectedClientsFullData:', preloadedClients.length);
            
            // Resetear el flag después de un delay
            setTimeout(() => {
              isUpdatingSelectionRef.current = false;
            }, 200);
          } else {
            console.log('[EditListModal] No hay contacts en la respuesta, limpiando selección');
            setSelectedClients([]);
            setSelectedClientsFullData([]);
          }
        } catch (error) {
          console.error('[EditListModal] Error loading list contacts:', error);
          // Si falla, limpiar la selección
          setSelectedClients([]);
          setSelectedClientsFullData([]);
        } finally {
          setLoadingListContacts(false);
        }
      }
    };
    
    loadListContacts();
  }, [activeStep, list]);

  // Función unificada para cargar clientes (B2C y B2B) usando EmailListService
  const loadClients = useCallback(async (page, pageSize, search, type) => {
    try {
      setClientLoading(true);
      console.log('[EditListModal] Loading clients:', { page, pageSize, search, type });
      
      const response = await EmailListService.getClients(page, pageSize, search, type);
      
      console.log('[EditListModal] Clients loaded:', {
        clientsCount: response.clientsCount || 0,
        total: response.total || 0,
        page: response.page || page,
        pageSize: response.pageSize || pageSize
      });
      
      // Mapear los clientes al formato esperado por el DataGrid
      const mappedClients = (response.clients || []).map((client) => {
        const clientType = client.type || client.clientType || 'B2C';
        const clientId = String(client.id || client.clientId || '').trim();
        
        return {
          id: clientType === 'B2B' ? `b2b_${clientId}` : `b2c_${clientId}`,
          clientId: clientId,
          clientType: clientType,
          displayName: client.name || client.displayName || `Cliente ${clientId}`,
          identifier: clientId,
          email: client.email || client.Email || client.emailAddress || client.mail || '',
          ...client
        };
      });
      
      setClients(mappedClients);
      setClientTotalRows(response.total || 0);
    } catch (error) {
      console.error('[EditListModal] Error loading clients:', error);
      setClients([]);
      setClientTotalRows(0);
    } finally {
      setClientLoading(false);
    }
  }, []);

  // Cargar clientes cuando se está en el paso 1 (para la tabla) - unificado
  useEffect(() => {
    if (activeStep === 1 && open && !searchTimeoutRef.current) {
      loadClients(
        clientPaginationModel.page + 1, 
        clientPaginationModel.pageSize, 
        clientSearchTerm, 
        clientTypeFilter
      );
    }
  }, [activeStep, open, clientPaginationModel.page, clientPaginationModel.pageSize, clientTypeFilter, loadClients, clientSearchTerm]);

  // Debounce para búsqueda de clientes
  useEffect(() => {
    if (activeStep === 1 && open) {
      // Limpiar timeout anterior si existe
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Resetear a página 0 cuando cambia el término de búsqueda
      if (clientPaginationModel.page !== 0) {
        setClientPaginationModel(prev => ({ ...prev, page: 0 }));
      }
      
      // Crear nuevo timeout con debounce de 500ms
      searchTimeoutRef.current = setTimeout(() => {
        loadClients(1, clientPaginationModel.pageSize, clientSearchTerm, clientTypeFilter);
        searchTimeoutRef.current = null;
      }, 500);
      
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [activeStep, open, clientSearchTerm, clientTypeFilter, clientPaginationModel.pageSize, loadClients]);

  // Combinar clientes según el filtro - mapear desde los datos unificados
  const displayClients = React.useMemo(() => {
    return clients;
  }, [clients]);

  // Columnas para la tabla de clientes
  const clientColumns = [
    {
      field: 'displayName',
      headerName: t('name') || 'Nombre',
      flex: 1.5,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.row.displayName || params.row.name || `Cliente ${params.row.clientId}`}
          </Typography>
          <Chip 
            label={params.row.clientType} 
            size="small" 
            color={params.row.clientType === 'B2B' ? 'primary' : 'secondary'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
      )
    },
    {
      field: 'email',
      headerName: t('email') || 'Email',
      flex: 1.5,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {params.row.email || params.row.Email || params.row.emailAddress || '-'}
          </Typography>
        </Box>
      )
    }
  ];

  const handleClientSelectionChange = (newSelection) => {
    // Prevenir actualizaciones mientras se está actualizando desde el backend
    if (isUpdatingSelectionRef.current) {
      console.log('[EditListModal] Update in progress, ignoring selection change');
      return;
    }
    
    console.log('[EditListModal] Clients selected in table:', newSelection.length);
    console.log('[EditListModal] Current selectedClients:', selectedClients.length);
    console.log('[EditListModal] Current selectedClientsFullData:', selectedClientsFullData.length);
    
    // Obtener los clientes que están en la tabla actual
    const clientsInTable = displayClients.filter(c => newSelection.includes(c.id));
    
    // Separar en clientes que ya estaban seleccionados (precargados) y nuevos
    const existingClientIds = selectedClients;
    const preloadedClientsNotInTable = selectedClientsFullData.filter(c => !displayClients.some(tc => tc.id === c.id));
    const existingClientsInTable = selectedClientsFullData.filter(c => displayClients.some(tc => tc.id === c.id) && newSelection.includes(c.id));
    const newClientIds = newSelection.filter(id => !existingClientIds.includes(id));
    const newClientsData = clientsInTable.filter(c => newClientIds.includes(c.id));
    
    // Combinar: precargados que no están en la tabla + existentes que siguen seleccionados + nuevos
    const combinedSelection = [
      ...preloadedClientsNotInTable,
      ...existingClientsInTable,
      ...newClientsData
    ];
    
    const combinedIds = combinedSelection.map(c => c.id);
    
    // Verificar si realmente hay cambios antes de actualizar
    const currentIds = selectedClients;
    const idsChanged = currentIds.length !== combinedIds.length || 
                       !currentIds.every(id => combinedIds.includes(id)) ||
                       !combinedIds.every(id => currentIds.includes(id));
    
    if (!idsChanged) {
      console.log('[EditListModal] No changes detected, skipping update');
      return;
    }
    
    console.log('[EditListModal] Combined selection - preloaded not in table:', preloadedClientsNotInTable.length, 
                'existing in table:', existingClientsInTable.length, 
                'new from table:', newClientsData.length,
                'total:', combinedSelection.length);
    
    isUpdatingSelectionRef.current = true;
    setSelectedClients(combinedIds);
    setSelectedClientsFullData(combinedSelection);
    
    // Resetear el flag después de un pequeño delay para permitir que React actualice
    setTimeout(() => {
      isUpdatingSelectionRef.current = false;
    }, 100);
  };

  const handleRemoveClient = (clientId) => {
    console.log('[EditListModal] Removing client:', clientId);
    const updatedClients = selectedClients.filter(id => id !== clientId);
    const updatedClientsFullData = selectedClientsFullData.filter(c => c.id !== clientId);
    setSelectedClients(updatedClients);
    setSelectedClientsFullData(updatedClientsFullData);
  };

  const handleNext = () => {
    console.log('[EditListModal] Moving to next step:', {
      currentStep: activeStep,
      nextStep: activeStep + 1
    });
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    console.log('[EditListModal] Moving to previous step:', {
      currentStep: activeStep,
      previousStep: activeStep - 1
    });
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Prevenir doble llamada
    if (saving) {
      console.warn('[EditListModal] Save already in progress, ignoring duplicate call');
      return;
    }
    
    if (!formData.name.trim()) {
      console.warn('[EditListModal] Cannot save: name is empty');
      return;
    }
    
    if (!list || !list.id) {
      console.warn('[EditListModal] Cannot save: no list ID');
      return;
    }
    
    setSaving(true);
    
    try {
      // Obtener los emails de los clientes seleccionados
      const clientEmails = selectedClientsFullData
        .map((client, index) => {
          // Intentar obtener el email de diferentes campos posibles
          const email = client.email || client.Email || client.emailAddress || client.mail || null;
          
          // Log para debug si no se encuentra email
          if (!email) {
            console.warn(`[EditListModal] Client ${index} has no email:`, {
              id: client.id,
              displayName: client.displayName,
              availableKeys: Object.keys(client)
            });
          }
          
          return email;
        })
        .filter(email => {
          // Filtrar emails válidos
          if (!email) return false;
          if (typeof email !== 'string') return false;
          const trimmed = email.trim();
          if (trimmed === '' || trimmed === 'null' || trimmed === '-' || trimmed.toLowerCase() === 'null') return false;
          if (!trimmed.includes('@')) return false;
          return true;
        });
      
      console.log('[EditListModal] Saving list - Debug:', {
        totalClientsSelected: selectedClientsFullData.length,
        clientsWithEmail: selectedClientsFullData.filter(c => {
          const email = c.email || c.Email || c.emailAddress || c.mail;
          return email && typeof email === 'string' && email.trim() !== '' && email.trim() !== 'null' && email.trim() !== '-' && email.includes('@');
        }).length,
        emailsExtracted: clientEmails.length,
        emails: clientEmails
      });
      
      // Preparar los datos para actualizar la lista
      const listData = {
        name: formData.name.trim(),
        description: formData.description || '',
        emails: clientEmails
      };
      
      console.log('[EditListModal] Saving list - Request:', {
        listId: list.id,
        name: listData.name,
        description: listData.description,
        emailsCount: listData.emails.length,
        emails: listData.emails
      });
      
      await EmailListService.updateList(list.id, listData);
      console.log('[EditListModal] List updated successfully');
      
      // Llamar a onSave con los datos actualizados
      if (onSave) {
        onSave(listData);
      }
      
      // Limpiar y cerrar
      setFormData({ name: '', description: '' });
      setSelectedClients([]);
      setSelectedClientsFullData([]);
      setActiveStep(0);
      onClose();
    } catch (error) {
      console.error('[EditListModal] Error updating list:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
      const menuElement = document.querySelector('.MuiMenu-paper, .MuiPopover-paper');
      if (menuElement) {
        return;
      }
      return;
    }
    setActiveStep(0);
    setFormData({ name: '', description: '' });
    setSelectedClients([]);
    setSelectedClientsFullData([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('edit')}
          {t('edit_list') || 'Editar Lista'}
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            padding: 0,
            borderRadius: '50%',
            '&:hover': {
              borderRadius: '50%'
            }
          }}
        >
          {getIcon('close')}
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Paso 0: Detalles de la Lista */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('list_details') || 'Detalles de la Lista'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('enter_list_details') || 'Ingresa el nombre y descripción de la lista'}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('name') || 'Nombre'}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder={t('list_name_placeholder') || 'Nombre de la lista'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('description') || 'Descripción'}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('list_description_placeholder') || 'Descripción de la lista'}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 1: Agregar clientes */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('add_clients_to_list') || 'Agregar Clientes a la Lista'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('select_clients_help') || 'Selecciona los clientes que deseas agregar a esta lista. Puedes buscar y filtrar por tipo de cliente.'}
            </Typography>

            {/* Loading mientras se cargan los contacts de la lista */}
            {loadingListContacts ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 6,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('loading_list_clients') || 'Cargando clientes de la lista...'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Sección 1: Clientes ya seleccionados */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    mb: 3,
                    bgcolor: 'grey.50', 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {t('selected_clients') || 'Clientes Seleccionados'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedClients.length} {selectedClients.length === 1 ? t('client') || 'cliente' : t('clients') || 'clientes'} {t('selected') || 'seleccionado(s)'}
                      </Typography>
                    </Box>
                    {selectedClientsFullData.length > 0 && (
                      <Chip 
                        label={selectedClientsFullData.length}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                  
                  {selectedClientsFullData.length > 0 ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1.5, 
                        maxHeight: 150, 
                        overflowY: 'auto', 
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 60
                      }}
                    >
                      {selectedClientsFullData.map((client) => (
                        <Chip
                          key={client.id}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {client.displayName || client.name || `Cliente ${client.identifier}`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                ({client.clientType})
                              </Typography>
                            </Box>
                          }
                          onDelete={() => handleRemoveClient(client.id)}
                          color={client.clientType === 'B2B' ? 'primary' : 'secondary'}
                          variant="filled"
                          size="medium"
                          sx={{ 
                            '& .MuiChip-deleteIcon': {
                              fontSize: '1.1rem',
                              color: 'inherit'
                            },
                            fontWeight: 'medium'
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('no_clients_selected_yet') || 'No hay clientes seleccionados aún. Selecciona clientes en la tabla de abajo.'}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Sección 2: Tabla para agregar/quitar clientes */}
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {t('select_clients_to_add') || 'Seleccionar Clientes para Agregar'}
                    </Typography>
                    
                    {/* Filtros */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label={t('search') || 'Buscar'}
                        variant="outlined"
                        size="small"
                        value={clientSearchTerm}
                        onChange={(e) => setClientSearchTerm(e.target.value)}
                        sx={{ width: 200 }}
                        placeholder={t('search_clients') || 'Buscar por nombre, email, etc.'}
                      />
                      
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('client_type') || 'Tipo de Cliente'}</InputLabel>
                        <Select
                          value={clientTypeFilter}
                          label={t('client_type') || 'Tipo de Cliente'}
                          onChange={(e) => setClientTypeFilter(e.target.value)}
                        >
                          <MenuItem value="all">{t('all') || 'Todos'}</MenuItem>
                          <MenuItem value="b2c">B2C</MenuItem>
                          <MenuItem value="b2b">B2B</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                      rows={displayClients}
                      columns={clientColumns}
                      getRowId={(row) => row.id}
                      checkboxSelection
                      onRowSelectionModelChange={handleClientSelectionChange}
                      rowSelectionModel={selectedClients}
                      paginationModel={clientPaginationModel}
                      onPaginationModelChange={setClientPaginationModel}
                      pageSizeOptions={[10, 25, 50]}
                      loading={clientLoading}
                      disableRowSelectionOnClick={false}
                      rowCount={clientTotalRows}
                      paginationMode="server"
                    />
                  </Box>
                </Paper>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined">
            {t('back')}
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={
              loadingListContacts || 
              (activeStep === 0 && (!formData.name.trim() || !formData.description.trim()))
            }
          >
            {t('next')}
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : getIcon('checkmarkDone')}
          >
            {saving ? (t('saving') || 'Guardando...') : t('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditListModal;
